import { FunctionComponent } from "react";

import { faClock } from "@fortawesome/pro-regular-svg-icons";

import { Icon } from "@/components";
import { classNames } from "@/utils/classNames";
import { ListType } from "@/enum/list.enum";

import { List } from "../../schema/list";

import { formatAssignedCount, getListStatusTone } from "./utils";

interface ListPickerRowProps {
	list: Partial<List>;
	isSelected: boolean;
	isArchived?: boolean;
	onSelect: (list: Partial<List>) => void;
}

const STATUS_DOT_CLASS: Record<string, string> = {
	active: "bg-status-success",
	paused: "bg-status-warning",
};

/** A single list row in the picker — radio indicator, type icon, name, count, status dot. */
export const ListPickerRow: FunctionComponent<ListPickerRowProps> = ({
	list,
	isSelected,
	isArchived = false,
	onSelect,
}) => {
	const tone = getListStatusTone(list.campaignStatus);
	const count = formatAssignedCount(list);
	const isRunning = list.type === ListType.ON_GOING;

	return (
		<button
			className={classNames(
				"w-full grid grid-cols-[16px_14px_1fr_auto_14px] items-center gap-2 h-11 px-3 text-left duration-200",
				isSelected ? "bg-blue-action-200" : "hover:bg-gray-100"
			)}
			data-test="list-picker-row"
			type="button"
			onClick={() => onSelect(list)}
		>
			{/* Radio-style select indicator */}
			<span
				className={classNames(
					"flex items-center justify-center w-4 h-4 rounded-full border",
					isSelected ? "border-brand-light bg-brand-light" : "border-border bg-white"
				)}
			>
				{isSelected && <span className="w-1.5 h-1.5 rounded-full bg-white" />}
			</span>

			{/* Type icon — clock for running (auto-updating) lists */}
			<span className="flex items-center justify-center text-gray-500">
				{isRunning && <Icon className="w-3.5 h-3.5" icon={faClock} />}
			</span>

			{/* Name */}
			<span
				className={classNames(
					"truncate text-sm font-medium",
					isArchived ? "text-gray-500 font-normal" : "text-gray-900"
				)}
			>
				{list.name}
			</span>

			{/* assigned/total count */}
			<span className="text-xs font-medium tabular-nums text-text-meta whitespace-nowrap">
				{count ?? ""}
			</span>

			{/* Status dot */}
			<span className="flex items-center justify-center">
				{tone !== "none" && !isArchived && (
					<span className={classNames("w-[7px] h-[7px] rounded-full", STATUS_DOT_CLASS[tone])} />
				)}
			</span>
		</button>
	);
};
